import { Assets, Container, Matrix, MeshSimple, Sprite, Texture } from "pixi.js";
import { buildStripGeometry } from "./deform.js";
import type { CompiledAttachment, CompiledSkeleton, PoseFrame } from "./types.js";

type AtlasTextures = Record<string, Record<string, Texture>>;
type SkeletonRenderable = Sprite | MeshSimple;

function attachmentMatrix(attachment: CompiledAttachment): [number, number, number, number, number, number] {
  const { x, y, rotation, scaleX, scaleY } = attachment.transform;
  return [rotation[0] * scaleX, rotation[1] * scaleX, -rotation[1] * scaleY, rotation[0] * scaleY, x, y];
}

function multiplyMatrix(
  left: ArrayLike<number>,
  right: ArrayLike<number>,
): Matrix {
  const [a, b, c, d, tx, ty] = [left[0], left[1], left[2], left[3], left[4], left[5]];
  const [ea, eb, ec, ed, etx, ety] = [right[0], right[1], right[2], right[3], right[4], right[5]];
  return new Matrix(a * ea + c * eb, b * ea + d * eb, a * ec + c * ed, b * ec + d * ed, a * etx + c * ety + tx, b * etx + d * ety + ty);
}

export class PixiSkeletonView {
  readonly container = new Container();
  private readonly renderables: Array<SkeletonRenderable | null>;
  private skinIndex = -1;

  constructor(readonly definition: CompiledSkeleton, private readonly atlases: AtlasTextures) {
    this.container.sortableChildren = true;
    this.renderables = definition.slots.map(() => null);
  }

  static async load(definition: CompiledSkeleton, compiledBundleUrl: string): Promise<PixiSkeletonView> {
    const atlases: AtlasTextures = {};
    await Promise.all(Object.entries(definition.atlases).map(async ([id, atlasPath]) => {
      const resource = await Assets.load(new URL(atlasPath, compiledBundleUrl).toString()) as { textures?: Record<string, Texture> };
      if (!resource.textures) throw new Error(`Atlas ${id} did not load as a Pixi spritesheet`);
      atlases[id] = resource.textures;
    }));
    return new PixiSkeletonView(definition, atlases);
  }

  createInstance(skinId = this.definition.skins[0]?.id ?? ""): PixiSkeletonView {
    const instance = new PixiSkeletonView(this.definition, this.atlases);
    instance.setSkin(skinId);
    return instance;
  }

  setSkin(skinId: string): void {
    const index = this.definition.skins.findIndex((skin) => skin.id === skinId);
    if (index < 0) throw new Error(`Unknown skeletal skin: ${skinId}`);
    this.skinIndex = index;
    const skin = this.definition.skins[index];
    for (let slot = 0; slot < this.definition.slots.length; slot += 1) {
      const attachment = skin.attachments[slot];
      const previous = this.renderables[slot];
      if (previous) {
        this.container.removeChild(previous);
        previous.destroy();
        this.renderables[slot] = null;
      }
      if (!attachment) {
        continue;
      }
      const texture = this.atlases[attachment.atlas]?.[attachment.frame];
      if (!texture) throw new Error(`Skin ${skinId} cannot resolve ${attachment.atlas}:${attachment.frame}`);
      let renderable: SkeletonRenderable;
      if (attachment.deform) {
        const bone = this.definition.bones[this.definition.slots[slot].bone];
        const endpoint = bone.sockets[attachment.deform.endSocket];
        const width = texture.orig.width || texture.width;
        const height = texture.orig.height || texture.height;
        const geometry = buildStripGeometry(attachment.deform, endpoint, { width, height });
        const mesh = new MeshSimple({ texture, ...geometry });
        mesh.autoUpdate = false;
        renderable = mesh;
      } else {
        const sprite = new Sprite(texture);
        const width = texture.orig.width || texture.width;
        const height = texture.orig.height || texture.height;
        sprite.anchor.set(attachment.pivot.x / width, attachment.pivot.y / height);
        renderable = sprite;
      }
      this.renderables[slot] = renderable;
      this.container.addChild(renderable);
    }
  }

  apply(frame: PoseFrame): void {
    if (this.skinIndex < 0) this.setSkin(this.definition.skins[0]?.id ?? "");
    const skin = this.definition.skins[this.skinIndex];
    for (let slot = 0; slot < this.definition.slots.length; slot += 1) {
      const renderable = this.renderables[slot];
      const attachment = skin.attachments[slot];
      if (!renderable || !attachment) continue;
      const bone = this.definition.slots[slot].bone;
      const worldOffset = bone * 6;
      const matrix = multiplyMatrix(frame.world.slice(worldOffset, worldOffset + 6), attachmentMatrix(attachment));
      renderable.setFromMatrix(matrix);
      renderable.alpha = Math.max(0, Math.min(1, frame.slotVisibility[slot]));
      renderable.visible = renderable.alpha > 0.001;
      renderable.zIndex = frame.slotOrder[slot];
    }
    this.container.sortChildren();
  }

  setSlotTexture(slotId: string, texture: Texture): void {
    const slot = this.definition.slots.findIndex((candidate) => candidate.id === slotId);
    if (slot < 0) throw new Error(`Unknown skeletal slot: ${slotId}`);
    const renderable = this.renderables[slot];
    if (renderable) renderable.texture = texture;
  }

  frameForSlot(slotId: string): string | null {
    const slot = this.definition.slots.findIndex((candidate) => candidate.id === slotId);
    if (slot < 0) throw new Error(`Unknown skeletal slot: ${slotId}`);
    return this.definition.skins[this.skinIndex]?.attachments[slot]?.frame ?? null;
  }

  destroy(options: Parameters<Container["destroy"]>[0] = { children: true }): void {
    this.container.destroy(options);
  }
}
